import maplibregl from 'maplibre-gl'
import mlcontour from 'maplibre-contour'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { DEM_URL, DEM_MAX_ZOOM } from './dem-config.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'
const SOURCE_ID = 'contour-source'
const LINE_LAYER_ID = 'contour-lines'
const LABEL_LAYER_ID = 'contour-labels'
const CONTOUR_LAYER = 'contours'
const DEFAULT_OPACITY = 0.5
const DEFAULT_COLOR = '#795548'

// Thresholds are in the OUTPUT unit (feet or meters, after multiplier is applied).
// [minor, major] — minor lines are thin, major lines are bold + labeled.

const IMPERIAL_THRESHOLDS: { [zoom: number]: number[] } = {
  9: [1000], // 1000ft only
  10: [500, 2500], // 500ft minor, 2500ft index
  11: [200, 1000], // 200ft minor, 1000ft index
  12: [200, 1000],
  13: [100, 500], // 100ft minor, 500ft index
  14: [50, 200], // 50ft minor, 200ft index
  15: [50, 200],
}

const METRIC_THRESHOLDS: { [zoom: number]: number[] } = {
  9: [500], // 500m only
  10: [200, 1000], // 200m minor, 1000m index
  11: [100, 500], // 100m minor, 500m index
  12: [50, 250], // 50m minor, 250m index
  13: [20, 100], // 20m minor, 100m index
  14: [10, 50], // 10m minor, 50m index
  15: [10, 50],
}

class ContourService {
  private map: maplibregl.Map | null = null
  private demSource: InstanceType<typeof mlcontour.DemSource> | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _color = DEFAULT_COLOR
  private settingsStorage = new SettingsStorage()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get color(): string {
    return this._color
  }

  register(map: maplibregl.Map): void {
    this.map = map
    this.demSource = new mlcontour.DemSource({
      url: DEM_URL,
      encoding: 'terrarium',
      maxzoom: DEM_MAX_ZOOM,
      worker: true,
    })
    this.demSource.setupMaplibre(maplibregl)
  }

  unregister(): void {
    this.disable()
    this.demSource = null
    this.map = null
  }

  enable(): void {
    if (!this.map || !this.demSource || this._enabled) {
      return
    }
    this._enabled = true
    this.addSourceAndLayers()
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map) {
      return
    }
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
  }

  setColor(color: string): void {
    this._color = color
    if (!this.map) {
      return
    }
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-color', color)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-color', color)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
  }

  private addSourceAndLayers(): void {
    if (!this.map || !this.demSource) {
      return
    }

    const settings = this.settingsStorage.getSettingsWithDefaults()
    const isImperial = settings.unitSystem !== 'metric'
    const multiplier = isImperial ? 3.28084 : 1
    const unitSuffix = isImperial ? "'" : ' m'
    const thresholds = isImperial ? IMPERIAL_THRESHOLDS : METRIC_THRESHOLDS

    // Ensure glyphs are available for labels (needed on raster base layers)
    this.ensureGlyphs()

    const sourceUrl = this.demSource.contourProtocolUrl({
      multiplier,
      overzoom: 1,
      thresholds,
      elevationKey: 'ele',
      levelKey: 'level',
      contourLayer: CONTOUR_LAYER,
    })

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'vector',
        tiles: [sourceUrl],
        maxzoom: DEM_MAX_ZOOM,
      })
    }

    if (!this.map.getLayer(LINE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': CONTOUR_LAYER,
          paint: {
            'line-color': this._color,
            'line-opacity': this._opacity,
            'line-width': ['match', ['get', 'level'], 1, 1.5, 0.6],
          },
        },
        'contours'
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': CONTOUR_LAYER,
          filter: ['>', ['get', 'level'], 0],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['concat', ['to-string', ['get', 'ele']], unitSuffix],
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
            'text-max-angle': 30,
            'text-padding': 3,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': this._color,
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.8)',
            'text-halo-width': 0.8,
          },
        },
        'contours'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) {
      return
    }
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) {
      return
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.removeLayer(LABEL_LAYER_ID)
    }
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.removeLayer(LINE_LAYER_ID)
    }
    if (this.map.getSource(SOURCE_ID)) {
      this.map.removeSource(SOURCE_ID)
    }
  }
}

export const contourService = new ContourService()
